import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogSiteName?: string;
  ogLocale?: string;
  ogImage?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  ogImageAlt?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  jsonLd?: object | object[];
}

const commonJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Dentist',
  '@id': 'https://saiwaidental.jp/moyuk/#dentist',
  name: 'さいわいデンタルクリニック moyuk SAPPORO',
  alternateName: 'moyuk SAPPORO さいわいデンタル',
  url: 'https://saiwaidental.jp/moyuk/',
  logo: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/e75f1b64faffc9fadd220d1137aeae0f.png',
  image: [
    'https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1024x1536_v-fms_webp_996baba1-e344-482e-bb22-6b7533c2dcd6_small.webp',
    'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/3d52d482bd410f941ffe1170d483f6f5.jpeg',
  ],
  description: '札幌・大通駅直結のmoyuk SAPPORO 2Fにある歯科クリニック。マウスピース矯正の無料診断レポート・医療ホワイトニングを提供。治療を急かさない、患者が納得して判断できる環境を整えています。',
  telephone: '050-1809-4594',
  email: 'moyuksapporo@saiwaidental.jp',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '南2条西3丁目20番地 moyuk SAPPORO 2F',
    addressLocality: '札幌市中央区',
    addressRegion: '北海道',
    postalCode: '060-0062',
    addressCountry: 'JP',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.05756,
    longitude: 141.35059,
  },
  hasMap: 'https://maps.app.goo.gl/17UaDjpyrjUgHRmm7',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '10:00',
      closes: '18:00',
    },
  ],
  priceRange: '¥¥¥',
  currenciesAccepted: 'JPY',
  paymentAccepted: 'Cash, Credit Card',
  medicalSpecialty: 'Dentistry',
  availableService: [
    {
      '@type': 'MedicalProcedure',
      name: 'マウスピース矯正（インビザライン）',
      description: '無料診断レポートつき。あなた専用の矯正プラン比較レポートを作成します。',
      url: 'https://saiwaidental.jp/moyuk/mouthpiece/',
    },
    {
      '@type': 'MedicalProcedure',
      name: '医療ホワイトニング（ホワイトエッセンス）',
      description: '白さを医療として設計。初回で変化を感じなければ返金保証。',
      url: 'https://saiwaidental.jp/moyuk/whitening/',
    },
  ],
  sameAs: [
    'https://smartee.saiwaidental.net',
    'https://saiwaidental.jp/',
    'https://ohmagarisaiwaidental.jp/',
  ],
  parentOrganization: {
    '@type': 'Organization',
    name: 'さいわいデンタルクリニック',
    url: 'https://saiwaidental.jp/',
  },
};

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogSiteName,
  ogLocale,
  ogImage = 'https://storage.readdy-site.link/project_files/29fc8b34-3c4c-432d-b46d-09fae4b65d73/2bf287c9-069a-46f2-9533-905b637be523_ogp.jpg?v=d1adf34df040ff9cedc4890b2a96ca1f',
  ogImageWidth,
  ogImageHeight,
  ogImageAlt = 'moyuk SAPPORO さいわいデンタル｜マウスピース矯正・ホワイトニング 札幌',
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage = 'https://storage.readdy-site.link/project_files/29fc8b34-3c4c-432d-b46d-09fae4b65d73/2bf287c9-069a-46f2-9533-905b637be523_ogp.jpg?v=d1adf34df040ff9cedc4890b2a96ca1f',
  jsonLd,
}: SeoHeadProps) {
  useEffect(() => {
    const existing = document.querySelectorAll('script[data-page-jsonld]');
    existing.forEach((el) => el.remove());

    const merged = jsonLd
      ? [commonJsonLd, ...(Array.isArray(jsonLd) ? jsonLd : [jsonLd])]
      : [commonJsonLd];

    const graphSchema = {
      '@context': 'https://schema.org',
      '@graph': merged,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-jsonld', 'graph');
    script.textContent = JSON.stringify(graphSchema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-page-jsonld]').forEach((el) => el.remove());
    };
  }, [jsonLd]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:title" content={ogTitle ?? title} />
      <meta property="og:description" content={ogDescription ?? description} />
      <meta property="og:type" content={ogType} />
      {canonical && <meta property="og:url" content={canonical} />}
      {ogSiteName && <meta property="og:site_name" content={ogSiteName} />}
      {ogLocale && <meta property="og:locale" content={ogLocale} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImageWidth && <meta property="og:image:width" content={ogImageWidth} />}
      {ogImageHeight && <meta property="og:image:height" content={ogImageHeight} />}
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      {twitterCard && <meta name="twitter:card" content={twitterCard} />}
      {twitterTitle && <meta name="twitter:title" content={twitterTitle} />}
      {twitterDescription && <meta name="twitter:description" content={twitterDescription} />}
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}
    </>
  );
}